import type { SceneStimulusEntry } from "../config/scene-stimulus-schema";
import type { Direction } from "../core/types";
import { createSceneDecisionBlocks } from "./schedule";
import type {
  SceneBlockResult,
  SceneCandidate,
  SceneDecision,
  SceneResponse,
  SceneTrainingMode,
  SceneTrainingPhase,
  SceneTrainingSnapshot,
} from "./types";

export interface SceneAnswerOutcome {
  readonly response: SceneResponse;
  readonly sceneComplete: boolean;
}

export class SceneTrainingEngine {
  private readonly blocks: readonly (readonly SceneDecision[])[];
  private phase: SceneTrainingPhase = "preflight";
  private phaseBeforePause: SceneTrainingPhase | null = null;
  private instructionPage = 0;
  private sceneIndex = 0;
  private decisionIndex = 0;
  private consecutiveErrors = 0;
  private assistanceActive = false;
  private practiceErrors = 0;
  private responses: SceneResponse[] = [];
  private results: SceneBlockResult[] = [];

  constructor(
    readonly scenes: readonly SceneStimulusEntry[],
    readonly seed: number,
    readonly mode: SceneTrainingMode,
  ) {
    this.blocks = createSceneDecisionBlocks(scenes, seed, mode);
  }

  getPhase(): SceneTrainingPhase { return this.phase; }
  getInstructionPage(): number { return this.instructionPage; }
  getSceneIndex(): number { return this.sceneIndex; }
  getDecisionIndex(): number { return this.decisionIndex; }
  getSceneCount(): number { return this.blocks.length; }
  getDecisionCount(): number { return this.blocks[this.sceneIndex]?.length ?? 0; }
  getResults(): readonly SceneBlockResult[] { return this.results; }
  getResponses(): readonly SceneResponse[] { return this.responses; }
  isAssistanceActive(): boolean { return this.assistanceActive; }

  getCurrentDecision(): SceneDecision | null {
    return this.blocks[this.sceneIndex]?.[this.decisionIndex] ?? null;
  }

  getVisibleCandidates(): readonly SceneCandidate[] {
    return this.requireDecision().candidates;
  }

  startInstruction(): void {
    this.assertPhase("preflight");
    this.phase = "instruction";
  }

  setInstructionPage(page: number): void {
    this.assertPhase("instruction");
    if (!Number.isInteger(page) || page < 0 || page > 2) throw new RangeError("Instruction page must be 0–2");
    this.instructionPage = page;
  }

  completeInstruction(skipPractice: boolean): void {
    this.assertPhase("instruction");
    this.phase = skipPractice ? "scene_active" : "practice_active";
  }

  commitPractice(direction: Direction): { readonly correct: boolean; readonly errors: number } {
    this.assertPhase("practice_active");
    const candidate = this.requireDecision().candidates.find((item) => item.direction === direction);
    if (!candidate) throw new Error(`Direction ${direction} is not active`);
    if (candidate.isTarget) {
      this.phase = "scene_active";
      return { correct: true, errors: this.practiceErrors };
    }
    this.practiceErrors += 1;
    return { correct: false, errors: this.practiceErrors };
  }

  skipPractice(): void {
    this.assertPhase("practice_active");
    this.phase = "scene_active";
  }

  commitNonFinalAnswer(direction: Direction): SceneAnswerOutcome {
    this.assertPhase("scene_active");
    const decision = this.requireDecision();
    const candidate = decision.candidates.find((item) => item.direction === direction);
    if (!candidate) throw new Error(`Direction ${direction} is not active`);
    if (this.decisionIndex + 1 >= (this.blocks[this.sceneIndex]?.length ?? 0)) {
      throw new Error("The last decision must be committed as the final answer");
    }
    const response: SceneResponse = {
      sceneId: decision.targetScene.id,
      decisionIndex: decision.decisionIndex,
      selectedDirection: direction,
      selectedSceneId: candidate.sceneId,
      correct: candidate.isTarget,
      final: false,
      assisted: false,
    };
    this.responses.push(response);
    this.decisionIndex += 1;
    return { response, sceneComplete: false };
  }

  commitFinalAnswer(direction: Direction): SceneAnswerOutcome {
    this.assertPhase("scene_active");
    const decision = this.requireDecision();
    const candidate = decision.candidates.find((item) => item.direction === direction);
    if (!candidate) throw new Error(`Direction ${direction} is not active`);
    const response: SceneResponse = {
      sceneId: decision.targetScene.id,
      decisionIndex: decision.decisionIndex,
      selectedDirection: direction,
      selectedSceneId: candidate.sceneId,
      correct: candidate.isTarget,
      final: true,
      assisted: false,
    };
    this.responses.push(response);
    const sceneResponses = this.responses.filter((response) => response.sceneId === decision.targetScene.id);
    this.results.push({
      sceneId: decision.targetScene.id,
      sceneName: decision.targetScene.displayName,
      outcome: candidate.isTarget ? "success" : "failure",
      finalDecisionIndex: decision.decisionIndex,
      responses: sceneResponses,
    });
    this.phase = candidate.isTarget ? "reveal" : "scene_failed";
    return { response, sceneComplete: true };
  }

  completeReveal(): void {
    this.assertPhase("reveal");
    this.advanceSceneOrSummary();
  }

  completeFailedScene(): void {
    this.assertPhase("scene_failed");
    this.advanceSceneOrSummary();
  }

  private advanceSceneOrSummary(): void {
    if (this.sceneIndex + 1 >= this.blocks.length) {
      this.phase = "summary";
      return;
    }
    this.sceneIndex += 1;
    this.decisionIndex = 0;
    this.consecutiveErrors = 0;
    this.assistanceActive = false;
    this.phase = "scene_active";
  }

  pause(): void {
    if (!["practice_active", "scene_active", "reveal"].includes(this.phase)) {
      throw new Error(`Cannot pause while in ${this.phase}`);
    }
    this.phaseBeforePause = this.phase;
    this.phase = "paused";
  }

  resume(): void {
    this.assertPhase("paused");
    if (!this.phaseBeforePause) throw new Error("Missing phase before pause");
    this.phase = this.phaseBeforePause;
    this.phaseBeforePause = null;
  }

  endIncomplete(): void {
    if (["preflight", "summary", "incomplete"].includes(this.phase)) throw new Error(`Cannot end from ${this.phase}`);
    this.phase = "incomplete";
    this.phaseBeforePause = null;
  }

  toSnapshot(): SceneTrainingSnapshot {
    return {
      schemaVersion: 1,
      mode: this.mode,
      seed: this.seed,
      phase: this.phase,
      phaseBeforePause: this.phaseBeforePause,
      instructionPage: this.instructionPage,
      sceneIndex: this.sceneIndex,
      decisionIndex: this.decisionIndex,
      consecutiveErrors: this.consecutiveErrors,
      assistanceActive: this.assistanceActive,
      practiceErrors: this.practiceErrors,
      responses: [...this.responses],
      results: [...this.results],
    };
  }

  static fromSnapshot(scenes: readonly SceneStimulusEntry[], snapshot: SceneTrainingSnapshot): SceneTrainingEngine {
    if (snapshot.schemaVersion !== 1) throw new Error("Unsupported scene training snapshot");
    const engine = new SceneTrainingEngine(scenes, snapshot.seed, snapshot.mode);
    engine.phase = snapshot.phase;
    engine.phaseBeforePause = snapshot.phaseBeforePause;
    engine.instructionPage = snapshot.instructionPage;
    engine.sceneIndex = snapshot.sceneIndex;
    engine.decisionIndex = snapshot.decisionIndex;
    engine.consecutiveErrors = snapshot.consecutiveErrors;
    engine.assistanceActive = snapshot.assistanceActive;
    engine.practiceErrors = snapshot.practiceErrors;
    engine.responses = [...snapshot.responses];
    engine.results = [...snapshot.results];
    const blockLength = engine.blocks[engine.sceneIndex]?.length ?? 0;
    if (engine.sceneIndex > engine.blocks.length || engine.decisionIndex >= Math.max(blockLength, 1)) throw new Error("Snapshot indices are invalid");
    return engine;
  }

  private requireDecision(): SceneDecision {
    const decision = this.getCurrentDecision();
    if (!decision) throw new Error("No current scene decision");
    return decision;
  }

  private assertPhase(expected: SceneTrainingPhase): void {
    if (this.phase !== expected) throw new Error(`Expected ${expected}, received ${this.phase}`);
  }
}
